import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
  TagSchemaWithInterfaces,
} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {FieldType, RequiredLink} from 'schemas/fields/SchemaFieldConstants';
import {ClientInterfaceField} from 'schemas/schema-interface/SchemaInterfaceUtils';
import * as net from 'util/Net';
import {omit} from 'util/omitKey';
import * as TaskRunner from 'util/TaskRunner';

import {CollaboratorInfoGraphQL} from '../../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';
import {ImplementingSchemaField} from './AddSchemaInterfaceModal';

// Keep in sync with /src/benchling/schemas/schema_interfaces/api/schema_interfaces.py
export const SCHEMA_INTERFACES_BASE_URL = 'fieldsets';
const _SCHEMA_INTERFACE_SUB_URL = 'fieldsets';

export function schemaInterfacesURL(): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/`;
}

export function createSchemaInterfaceURL(): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/create`;
}

export function schemaInterfaceURL(interfaceId: string): string {
  return `/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}`;
}

export interface SchemaInterfaceFieldForRequest {
  systemName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  isMulti: boolean;
  requiredLink: RequiredLink | null;
  schemaFieldSelectorId: string | null;
  unitApiIdentifier?: string | null;
}

export function createSchemaInterface(args: {
  name: string;
  organizationId: string;
  sqlIdentifier: string | null;
  prefix: string;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  itemType: TagSchemaItemType | null;
  constraint?: SchemaConstraintCreatePayload | null;
  collaborators?: Array<SchemaCollaborator>;
  fields?: Array<SchemaInterfaceFieldForRequest>;
  schemaDescription?: string;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}`,
    data: args,
  });
}

export function convertToSchemaInterfaceFieldForRequest(
  field: ClientInterfaceField
): SchemaInterfaceFieldForRequest {
  return omit(field, 'apiId', 'id', 'isComputed', 'unitSymbol');
}

export function convertToSchemaConstraintCreatePayload(
  tagSchemaConstraint: ClientTagSchemaConstraint
): SchemaConstraintCreatePayload {
  const fields = tagSchemaConstraint.fields.map((field) => ({
    name: field.name,
  }));
  return {
    uniqueResidues: tagSchemaConstraint.uniqueResidues,
    uniqueCanonicalSmiles: tagSchemaConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: tagSchemaConstraint.areUniqueResiduesCaseSensitive,
    typeSpecificConstraintConfig: tagSchemaConstraint.typeSpecificConstraintConfig,
    fields,
  };
}

export function convertToSchemaConstraintUpdatePayload(
  tagSchemaConstraint: ClientTagSchemaConstraint
): SchemaConstraintUpdatePayload {
  const fields = tagSchemaConstraint.fields.map((field) => {
    const apiId = field.apiId;
    // TagSchemaConstraint's fields are defined to be a SchemaField | ClientSchemaField, where client schema field may not have an apiId.
    // Through the update entrypoint however, an apiId must be present.
    // Ideally the types would be refactored to make this unnecessary.
    assert(Boolean(apiId), 'Expected field to have an apiId');
    return {
      apiId: apiId!,
      name: field.name,
    };
  });
  return {
    uniqueResidues: tagSchemaConstraint.uniqueResidues,
    uniqueCanonicalSmiles: tagSchemaConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: tagSchemaConstraint.areUniqueResiduesCaseSensitive,
    typeSpecificConstraintConfig: tagSchemaConstraint.typeSpecificConstraintConfig,
    fields,
  };
}

export interface SchemaConstraintCreatePayload {
  uniqueResidues: boolean;
  uniqueCanonicalSmiles: boolean;
  areUniqueResiduesCaseSensitive: boolean;
  typeSpecificConstraintConfig?: TagSchemaConstraint['typeSpecificConstraintConfig'];
  fields: Array<{
    name: string;
  }>;
}

export interface SchemaConstraintUpdatePayload {
  uniqueResidues: boolean;
  uniqueCanonicalSmiles: boolean;
  areUniqueResiduesCaseSensitive: boolean;
  typeSpecificConstraintConfig?: TagSchemaConstraint['typeSpecificConstraintConfig'];
  fields: Array<{
    apiId: string;
    name: string;
  }>;
}

export interface UpdateSchemaInterfaceRequest {
  name?: string;
  sqlIdentifier?: string;
  itemType?: TagSchemaItemType | null;
  prefix?: string;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  constraint?: SchemaConstraintUpdatePayload | null;
  schemaDescription?: string;
}

export function updateSchemaInterface(
  interfaceId: string,
  data: UpdateSchemaInterfaceRequest
): Promise<{id: string}> {
  return net.patchJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}`,
    data,
  });
}

export function addSchemaInterfaceField({
  interfaceId,
  field,
}: {
  interfaceId: string;
  field: SchemaInterfaceFieldForRequest;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/fields`,
    data: {field},
  });
}

export function updateSchemaInterfaceField({
  interfaceId,
  fieldId,
  field,
}: {
  interfaceId: string;
  fieldId: string;
  field: SchemaInterfaceFieldForRequest;
}): JQueryPromise<{id: string}> {
  return TaskRunner.runTask({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/fields/${fieldId}`,
    data: JSON.stringify({field}),
    type: 'PATCH',
    dataType: 'json',
    contentType: 'application/json',
  });
}

export function removeSchemaInterfaceField(args: {interfaceId: string; fieldId: string}): Promise<{}> {
  return net.deletePromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${args.interfaceId}/fields/${args.fieldId}`,
  });
}

interface UpdateSchemaCollaboratorsRequest {
  collaborators: Array<SchemaCollaborator>;
}
interface UpdateInterfaceCollaboratorsResponse {
  interface: CollaboratorInfoGraphQL;
}

export function updateCollaborators(
  interfaceId: string,
  data: UpdateSchemaCollaboratorsRequest
): Promise<UpdateInterfaceCollaboratorsResponse> {
  return net.patchJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/collaborators`,
    data,
  });
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<void> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}:bulk-archive`,
    data: {ids, purpose},
  });
}

export function bulkUnarchive(ids: Array<string>): Promise<void> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}:bulk-unarchive`,
    data: {ids},
  });
}

/**
 * The edits required for a schema to conform to the interface it is adding.
 *
 * Currently, this is only updates to fields as all other properties are auto updated or not constrained by the interface.
 * This object follows the same shape as that for the normal update schema payload.
 */
interface AddSchemaAndConformSchemaRequest {
  fields?: Array<ImplementingSchemaField>;
}

interface AddInterfaceAndConformSchemaResponse {
  schema: TagSchemaWithInterfaces;
}

export function addInterfaceAndConformSchema(
  schemaId: string,
  schemaConformityUpdate: AddSchemaAndConformSchemaRequest,
  interfaceIds: Array<string>
): Promise<AddInterfaceAndConformSchemaResponse> {
  const data = {
    interfaces: interfaceIds.map((interfaceId) => ({apiId: interfaceId})),
    schema: {
      fields: schemaConformityUpdate.fields?.map((field) => ({
        ...field,
        name: field.displayName || field.name,
      })),
    },
  };
  return TaskRunner.postJSONPromise({
    data,
    url: `/1/api/schemas/${schemaId}/${_SCHEMA_INTERFACE_SUB_URL}/update-schema`,
  });
}

interface RemoveInterfaceSchemaAssocationResponse {
  interfaces: Array<string>;
}

export function removeInterfaceSchemaAssociation(
  schemaId: string,
  interfaceId: string,
  schemaPrefixReplacement?: string
): Promise<RemoveInterfaceSchemaAssocationResponse> {
  return TaskRunner.deleteJSONPromise({
    url: `/1/api/schemas/${schemaId}/${_SCHEMA_INTERFACE_SUB_URL}/${interfaceId}`,
    data: {schemaPrefixReplacement},
  });
}

export function setSchemaInterfaceNameTemplate(
  interfaceId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  shouldOrderPartsBySequence: boolean
): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/${SCHEMA_INTERFACES_BASE_URL}/${interfaceId}/name-template`,
    data: {nameTemplateParts, shouldOrderPartsBySequence},
  });
}
